import { React, classNames } from '@digsup/core-ui';
import PropTypes from 'prop-types';
import { Text } from '@digsup/components-text';
import { List } from '@digsup/components-list';
import { Icon } from '@digsup/components-icon';
import { Image } from '@digsup/components-image';
import { Button } from '@digsup/components-button';
import { Cell } from '@digsup/components-grid';
import { Collapse } from '@digsup/components-collapse';
import { Divider } from '@digsup/components-divider';

import freeAccImg from '@digsup/assets-images/src/free-plan.svg';
import standardAccImg from '@digsup/assets-images/src/standard-plan.svg';
import premiumAccImg from '@digsup/assets-images/src/premium-plan.svg';
import { styles } from './PricingCard.scss';

const cx = classNames.bind(styles);

const getAccountImg = type => {
  switch (type) {
    case 'free':
      return freeAccImg;
    case 'standard':
      return standardAccImg;
    case 'premium':
      return premiumAccImg;
    default:
      return null;
  }
};

const getFeatureItemIconProps = type => {
  switch (type) {
    case 'excluded':
      return { name: 'x', fill: 'charcoal', shapeColor: 'neutral' };
    case 'excluded-highlighted':
      return { name: 'x', fill: 'white', shapeColor: 'unicorn' };
    case 'included-highlighted':
      return {
        name: 'arrow-right-bold',
        fill: 'black',
        shapeColor: 'lenient',
      };
    default:
      return {
        name: 'checkmark-included',
        fill: 'white',
        shapeColor: 'lenient-dark',
      };
  }
};

export const PricingCard = ({
  type,
  title,
  price,
  priceSuffix,
  features,
  highlightedFeatures,
  href,
  onClick,
  ctaName,
  ctaVariant,
  shadow,
  border,
  ...props
}) => {
  const classes = cx('c-pricing-plans-card', {
    [`c-pricing-plans-card--${type}`]: type,
    [`c-pricing-plans-card--shadow-${shadow}`]: shadow,
    [`c-pricing-plans-card--border-${border}`]: border,
  });

  const mediaClasses = cx('c-pricing-plans-card__media', {
    [`c-pricing-plans-card__media--${type}`]: type,
  });

  const featuresListItems = items =>
    items.map((item, i) => {
      const iconProps = getFeatureItemIconProps(item.type);

      return (
        <List.Item key={i}>
          <List
            spacing="small"
            display="flex"
            nowrap
            valign="center"
            align="start">
            <List.Item>
              <Icon {...iconProps} shape="round" className="u-display-block" />
            </List.Item>
            <List.Item>
              <Text>{item.name}</Text>
            </List.Item>
          </List>
        </List.Item>
      );
    });

  const featuresList = features ? (
    <List spacing="large" className="u-display-inline-block">
      {featuresListItems(features)}
    </List>
  ) : null;

  const highlightedFeaturesList = highlightedFeatures ? (
    <List spacing="large" className="u-display-inline-block">
      {featuresListItems(highlightedFeatures)}
    </List>
  ) : null;

  return (
    <Cell columns={{ xsmall: 12, medium: 4 }}>
      <div className="c-pricing-plans-card-wrapper">
        <div className={classes} {...props}>
          <div className="c-pricing-plans-card__body">
            <Text
              size="sm"
              fontFamily="serif"
              fontWeight="semibold"
              align="start"
              marginBottom="small"
              className="u-margin-bottom-medium@medium">
              {title}
            </Text>
            <div className="u-text-align-left">
              <Text
                display="inline"
                size="2xs"
                fontWeight="semibold"
                align="start">
                {price}/
              </Text>
              <Text
                display="inline"
                size="6xs"
                fontWeight="semibold"
                align="start">
                mo
              </Text>
            </div>
            <Text
              size="7xs"
              align="start"
              className="u-margin-bottom-xlarge-stretched">
              {priceSuffix}
            </Text>

            <div className="c-pricing-plans-card__divider u-margin-bottom-xlarge-stretched u-margin-bottom-xxlarge@medium">
              <Divider />
            </div>
            <div className="u-margin-bottom-small u-margin-bottom-xlarge-stretched@medium">
              <div className="u-visuallyhidden@medium u-visuallyhidden@large u-visuallyhidden@xlarge">
                {highlightedFeaturesList}
                <div className="c-pricing-plans-card__features-collapse">
                  <Collapse
                    header={
                      <span className="u-color-digsup-blue">
                        What included?
                      </span>
                    }
                    isOpened={false}>
                    <div className="u-padding-top-xsmall u-padding-bottom-xlarge">
                      {featuresList}
                    </div>
                  </Collapse>
                </div>
              </div>
              <div className="u-visuallyhidden@xsmall u-visuallyhidden@small">
                {featuresList}
              </div>
            </div>
            <div
              className={cx('c-pricing-plans-card__cta', {
                't-danger': ctaVariant === 'default',
                [`c-pricing-plans-card__cta--${ctaVariant}`]: ctaVariant,
              })}>
              {href || onClick ? (
                <Button
                  onClick={onClick}
                  variant="primary"
                  href={href}
                  width="full">
                  {ctaName}
                </Button>
              ) : null}
            </div>

            <div className={mediaClasses}>
              <Image src={getAccountImg(type)} alt={`${type} account`} />
            </div>
          </div>
        </div>
      </div>
    </Cell>
  );
};

PricingCard.propTypes = {
  type: PropTypes.string,
  title: PropTypes.string,
  price: PropTypes.string,
  priceSuffix: PropTypes.string,
  features: PropTypes.array,
  highlightedFeatures: PropTypes.array,
  /**
   * Used for cta btn
   */
  onClick: PropTypes.func,
  /**
   * Used for cta btn
   */
  href: PropTypes.string,
  /**
   * Used for cta btn
   */
  ctaName: PropTypes.string,
  ctaVariant: PropTypes.oneOf(['default', 'primary']),
  shadow: PropTypes.oneOf(['none', 'default']),
  border: PropTypes.oneOf(['none', 'default']),
};

PricingCard.defaultProps = {
  shadow: 'default',
  border: 'none',
  ctaName: 'Get Started',
  ctaVariant: 'default',
};
